// Recovery helpers for stale-chunk failures after a deploy.
//
// Every production deploy replaces the hashed /assets chunks, so a tab
// opened before the deploy 404s when it lazy-loads a chunk it has not
// fetched yet. One reload picks up the new index.html with fresh refs.
//
// Deliberately NOT a window-level `vite:preloadError` listener: that event
// fires for EVERY failed dynamic import, including component-local ones
// (Deck Chat dock, RevenueCat SDK) that have their own inline boundaries
// and degrade gracefully without losing page state. A global listener
// would preempt those and reload the whole page - strictly worse than the
// inline banner. Instead, RouteErrorBoundary calls these helpers only for
// failures that escaped every local boundary and reached the route level.

const RELOAD_GUARD_KEY = 'cb-chunk-reload-at'
const RELOAD_GUARD_WINDOW_MS = 60_000

// Browser wording for a failed dynamic import varies; Vite's own CSS
// preload failure has its own message. Matched loosely on purpose.
const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /unable to preload css/i, // Vite preload helper
]

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return CHUNK_ERROR_PATTERNS.some((p) => p.test(message))
}

function lastAutoReloadAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0
  } catch {
    // sessionStorage can throw (private mode, storage disabled); treat as
    // "just reloaded" so we never risk an unguarded reload loop.
    return Date.now()
  }
}

// Pure read of the guard, safe to call from React's render phase (which
// StrictMode double-invokes). The actual write + reload happens in
// attemptChunkReload, from a commit-phase lifecycle.
export function canAutoReload(): boolean {
  return Date.now() - lastAutoReloadAt() >= RELOAD_GUARD_WINDOW_MS
}

// At most one automatic reload per guard window per tab. Returns true when
// a reload was initiated (caller should render a quiet placeholder, not a
// fallback that would flash before navigation); false hands the error to
// the visible fallback with its manual reload button.
export function attemptChunkReload(): boolean {
  if (Date.now() - lastAutoReloadAt() < RELOAD_GUARD_WINDOW_MS) return false
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    return false
  }
  window.location.reload()
  return true
}
